import gsap from "gsap";

const DELTA_LINE_MULTIPLIER = 8;
const DELTA_PAGE_MULTIPLIER = 24;
const MAX_WHEEL_DELTA = 24;

function constrain (target, bounds) {
  const x = gsap.getProperty(target, "x");
  const y = gsap.getProperty(target, "y");
  const z = gsap.getProperty(target, "z");

  const cX = gsap.utils.clamp(-bounds.x, bounds.x * 0.1, x);
  const cY = gsap.utils.clamp(-bounds.y, 0, y);
  const cZ = gsap.utils.clamp(-bounds.z, bounds.z * 0.4, z);

  gsap.set(target, { x: cX, y: cY, z: cZ });
}

function limit(delta, max_delta) {
  return Math.sign(delta) * Math.min(max_delta, Math.abs(delta));
}

function midpoint (pointers) {
  const [point1, point2] = pointers;

  return {
    x: (point1.clientX + point2.clientX) * 0.5,
    y: (point1.clientY + point2.clientY) * 0.5
  }
}

function normalizeWheel(e) {
  let dx = e.deltaX;
  let dy = e.deltaY;

  if (e.shiftKey && dx === 0) {
    [dx, dy] = [dy, dx];
  }

  if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    dx *= DELTA_LINE_MULTIPLIER;
    dy *= DELTA_LINE_MULTIPLIER;
  } else if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    dx *= DELTA_PAGE_MULTIPLIER;
    dy *= DELTA_PAGE_MULTIPLIER;
  }

  return [limit(dx, MAX_WHEEL_DELTA), limit(dy, MAX_WHEEL_DELTA)];
}

export default class Controls {
  constructor (container, target) {
    this.pointers = [];
    this.position = { x: 0, y: 0, z: 0 };
    this.distance = 0;
    this.container = container;
    this.target = target;
    this.timer;
    this.bounds;
    this.raf;

    this.boundHandleDown = this.handleDown.bind(this);
    this.boundHandleMove = this.handleMove.bind(this);
    this.boundHandleUp = this.handleUp.bind(this);
    this.boundHandleWheel = this.handleWheel.bind(this);

    window.addEventListener("resize", () => {
      this.setSizes();
    });

    this.setSizes();
  }

  init () {
    this.updatePosition({ x: this.bounds.x * 1.1, y: -this.bounds.y, z: 0 });
  }

  updateDistance () {
    this.distance = Math.round(Math.sqrt(Math.pow(this.pointers[1].clientX - this.pointers[0].clientX, 2) + Math.pow(this.pointers[1].clientY - this.pointers[0].clientY, 2)));
  }

  updatePointer (event) {
    const index = this.pointers.findIndex(({ pointerId }) => pointerId === event.pointerId);

    this.pointers[index] = event;
  }

  handleDown (event) {
    event.preventDefault();

    if (this.pointers.length === 0) {
      this.container.addEventListener("pointermove", this.boundHandleMove);
    }

    this.pointers.push(event);

    this.updatePointer(event);

    if (this.pointers.length === 2) {
      this.updateDistance();
    }
  }

  handleMove (event) {
    event.preventDefault();

    // If one pointer is down handle pan
    if (this.pointers.length === 1) {

      this.position = {
        x: event.clientX - this.pointers[0].x,
        y: event.clientY - this.pointers[0].y,
        z: 0
      }

      this.updatePosition(this.position);

      this.pointers[0] = event;

      return;
    }

    // If two pointers are down handle pinch gestures
    if (this.pointers.length === 2) {
      const prevDistance = this.distance;
      const prevPointers = this.pointers

      this.updatePointer(event);

      this.updateDistance();

      const prevPoint = midpoint(prevPointers);
      const point = midpoint(this.pointers)

      this.position = {
        x: point.x - prevPoint.x,
        y: point.y - prevPoint.y,
        z: this.distance - prevDistance
      };

      this.updatePosition(this.position);

      return;
    }
  }

  handleUp (event) {
    event.preventDefault();

    this.removePointer(event);

    if (this.pointers.length < 2) this.distance = 0;

    if (this.pointers.length === 0) {
      this.container.removeEventListener("pointermove", this.boundHandleMove);
    }
  }

  handleWheel (event) {
    event.preventDefault();

    const [dx, dy] = normalizeWheel(event);

    this.updatePosition({ x: 0, y: 0, z: (dy * -1) });
  }

  updatePosition ({ x, y, z }) {
    gsap.set(this.target, { x:`+=${x}`, y:`+=${y}`, z:`+=${z}` });
  
    constrain(this.target, this.bounds)
  }

  removePointer (event) {
    const index = this.pointers.findIndex(({ pointerId }) => pointerId === event.pointerId);

    if (index >= 0) this.pointers.splice(index, 1);
  }

  setListeners () {
    this.container.addEventListener("pointerdown", this.boundHandleDown);
    this.container.addEventListener("wheel", this.boundHandleWheel);
    this.container.addEventListener("pointerup", this.boundHandleUp);
    this.container.addEventListener("pointercancel", this.boundHandleUp);
    this.container.addEventListener("pointerout", this.boundHandleUp);
    this.container.addEventListener("pointerleave", this.boundHandleUp);
  }

  setSizes () {
    const rect = this.target.getBoundingClientRect();

    this.bounds = {
      x: rect.width - window.innerWidth,
      y: rect.height - window.innerHeight,
      z: 300
    }
  }
}